import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";

export const PIPELINE_STAGES = [
  { id: "new", label: "New Lead", color: "gray" },
  { id: "researched", label: "Researched", color: "blue" },
  { id: "outreach_sent", label: "Outreach Sent", color: "yellow" },
  { id: "replied", label: "Replied", color: "purple" },
  { id: "demo_scheduled", label: "Demo Scheduled", color: "green" },
  { id: "converted", label: "Converted", color: "emerald" },
  { id: "not_interested", label: "Not Interested", color: "red" },
];

export const GrowthService = {
  async getLeads(userId) {
    const q = query(
      collection(db, "growthLeads"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async getLead(leadId) {
    const snap = await getDoc(doc(db, "growthLeads", leadId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async createLead(userId, data) {
    const lead = {
      userId,
      name: data.name || "",
      email: data.email || "",
      instagram: data.instagram || "",
      website: data.website || "",
      city: data.city || "",
      eventName: data.eventName || "",
      eventType: data.eventType || "",
      estimatedVendorCount: data.estimatedVendorCount || null,
      contactForm: data.contactForm || "",
      source: data.source || "manual",
      status: "new",
      score: null,
      notes: data.notes || "",
      research: null,
      outreach: null,
      lastContactedAt: null,
      nextFollowUpAt: null,
      demoScheduledAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, "growthLeads"), lead);
    return { id: ref.id, ...lead };
  },

  async updateLead(leadId, updates) {
    const ref = doc(db, "growthLeads", leadId);
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
  },

  async deleteLead(leadId) {
    await deleteDoc(doc(db, "growthLeads", leadId));
  },

  async updateStage(leadId, stage) {
    await GrowthService.updateLead(leadId, { status: stage });
  },

  async saveResearch(leadId, research) {
    await GrowthService.updateLead(leadId, {
      research,
      status: "researched",
      score: research.score || null,
    });
  },

  async saveOutreach(leadId, outreach) {
    await GrowthService.updateLead(leadId, { outreach });
  },

  async markOutreachSent(leadId, channel) {
    await GrowthService.updateLead(leadId, {
      status: "outreach_sent",
      lastContactedAt: serverTimestamp(),
      lastChannel: channel,
    });
  },

  async bulkImport(userId, rows) {
    const batch = writeBatch(db);
    const results = [];
    for (const row of rows) {
      const ref = doc(collection(db, "growthLeads"));
      const lead = {
        userId,
        name: row.name || "",
        email: row.email || "",
        instagram: row.instagram || "",
        website: row.website || "",
        city: row.city || "",
        eventName: row.eventName || row.event_name || "",
        eventType: row.eventType || row.event_type || "",
        estimatedVendorCount: row.estimatedVendorCount || row.vendor_count || null,
        source: "csv_import",
        status: "new",
        score: null,
        notes: "",
        research: null,
        outreach: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      batch.set(ref, lead);
      results.push({ id: ref.id, ...lead });
    }
    await batch.commit();
    return results;
  },

  // ─── Winner examples (outreach that got a reply) ──────────────────────────

  async saveWinner(userId, lead) {
    if (!lead.outreach) return;
    const ref = collection(db, "growthWinners");
    await addDoc(ref, {
      userId,
      leadName: lead.name,
      eventName: lead.eventName || "",
      eventType: lead.eventType || "",
      city: lead.city || "",
      outreach: lead.outreach,
      research: lead.research || null,
      wonAt: serverTimestamp(),
    });
  },

  async getWinners(userId, limit = 5) {
    const q = query(
      collection(db, "growthWinners"),
      where("userId", "==", userId),
      orderBy("wonAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.slice(0, limit).map((d) => ({ id: d.id, ...d.data() }));
  },

  async getMetrics(userId) {
    const leads = await GrowthService.getLeads(userId);
    const total = leads.length;
    const byStage = {};
    PIPELINE_STAGES.forEach((s) => (byStage[s.id] = 0));
    leads.forEach((l) => {
      if (byStage[l.status] !== undefined) byStage[l.status]++;
    });
    const withResearch = leads.filter((l) => l.research).length;
    const withOutreach = leads.filter((l) => l.outreach).length;
    const replied = leads.filter((l) =>
      ["replied", "demo_scheduled", "converted"].includes(l.status)
    ).length;
    const converted = byStage.converted || 0;
    const demos = byStage.demo_scheduled || 0;
    return {
      total,
      byStage,
      withResearch,
      withOutreach,
      replied,
      converted,
      demos,
      responseRate: withOutreach > 0 ? Math.round((replied / withOutreach) * 100) : 0,
    };
  },
};
